import React, { useEffect, useState } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  SecondaryButton,
  TertiaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import AddInCircleIcon from '@grnhse/seedling/lib/birch/icons/add_in_circle';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { withLocalePrefix } from 'shared/utils/translation';
import type { FilterGroup } from 'talent_matching/types';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';
import AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import SingleFilter from 'talent_matching/components/filters/single_filter';
import styled from 'styled-components';

const t = withLocalePrefix('talent_matching.filter_row.question_filters');

const InlineAction = styled.div`
  align-self: flex-start;
  display: inline-flex;
`;

export const QuestionFiltersSection = () => {
  const { questionFilterConfig } = useTalentMatchingTabContext();
  const { setValue, watch } = useFormContext();
  const questionFilterGroups: FilterGroup[] =
    questionFilterConfig.filterGroups || [];
  const questionFilters = (watch('questionFilters') as AdvancedFilter[]) || [];

  const hasConfiguredFilters = questionFilters.some((filter: AdvancedFilter) =>
    Boolean(filter.type || filter.operator || filter.value)
  );
  const [isExpanded, setIsExpanded] = useState(hasConfiguredFilters);

  useEffect(() => {
    if (hasConfiguredFilters) {
      setIsExpanded(true);
    }
  }, [hasConfiguredFilters]);

  useEffect(() => {
    if (questionFilters.length === 0) {
      setIsExpanded(false);
    }
  }, [questionFilters.length]);

  const handleAddQuestionFilters = () => {
    setIsExpanded(true);
    if (questionFilters.length === 0) {
      setValue('questionFilters', [new AdvancedFilter()], {
        shouldDirty: true,
      });
    }
  };

  const handleAddFilter = () => {
    setValue('questionFilters', [...questionFilters, new AdvancedFilter()], {
      shouldDirty: true,
    });
  };

  const handleRemoveFilter = (index: number) => {
    const newFilters = [...questionFilters];
    newFilters.splice(index, 1);
    setValue('questionFilters', newFilters, { shouldDirty: true });
    if (newFilters.length === 0) {
      setIsExpanded(false);
    }
  };

  const handleRemoveQuestionFilters = () => {
    setValue('questionFilters', [], { shouldDirty: true });
    setIsExpanded(false);
  };

  if (!isExpanded) {
    return (
      <InlineAction>
        <SecondaryButton
          onClick={handleAddQuestionFilters}
          icon={AddInCircleIcon}
          size="md"
        >
          {t('add_question_filters')}
        </SecondaryButton>
      </InlineAction>
    );
  }

  return (
    <Stack spacing={1} alignItems="flex-start" sx={{ width: '100%' }}>
      <SecondaryBody medium>{t('title')}</SecondaryBody>
      <Stack spacing={1} alignItems="flex-start" sx={{ width: '100%' }}>
        {questionFilters.map((filter: AdvancedFilter, index: number) => (
          <SingleFilter
            key={filter.reactKey}
            filter={filter}
            onChange={(updatedFilter: AdvancedFilter) => {
              const newFilters = [...questionFilters];
              newFilters.splice(index, 1, updatedFilter);
              setValue('questionFilters', newFilters, { shouldDirty: true });
            }}
            onRemove={() => handleRemoveFilter(index)}
            typeOptions={questionFilterGroups}
          />
        ))}
        <InlineAction>
          <TertiaryButton
            onClick={handleAddFilter}
            icon={AddInCircleIcon}
            size="md"
          >
            {t('add_filter')}
          </TertiaryButton>
        </InlineAction>
      </Stack>
      <InlineAction>
        <TertiaryButton onClick={handleRemoveQuestionFilters} size="md">
          {t('remove_question_filters')}
        </TertiaryButton>
      </InlineAction>
    </Stack>
  );
};

export default QuestionFiltersSection;
